import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Input, Table, Spinner, Alert, FormGroup, Label, Row, Col
} from 'reactstrap';
import { esleStandFirma, clearEsleState } from '../../../../slices/standSlice';
import { standService } from '../../../../services/standService';
import { getFirmaTypes } from '../../../../services/firmaAramaService';

// Backlog #21 — this used to be a hardcoded, WRONG map (2=Dekorator here, but the
// backend's real firma_type 2 is Organizator; no Taseron option at all). Now sourced
// live from getFirmaTypes() (GET /firma/types), the same canonical call
// firmaAramaService/CreateFirmaModal already use elsewhere.

const ESLESME_SEVIYELERI = [
  { id: 1, label: '1' },
  { id: 2, label: '2' },
  { id: 3, label: '3' },
];

/**
 * FirmaEsleModal
 * Bir standa firma eşlemek için kullanılır.
 * Props:
 *   isOpen    - boolean
 *   toggle    - () => void
 *   standId   - number
 *   salonId   - number
 *   fuarId    - number
 *   onSuccess - () => void  (eşleme başarılı olduktan sonra çağrılır)
 */
const FirmaEsleModal = ({ isOpen, toggle, standId, salonId, fuarId, onSuccess }) => {
  const dispatch = useDispatch();
  const { esle } = useSelector((state) => state.stand);

  const [search, setSearch] = useState('');
  const [firmaList, setFirmaList] = useState([]);
  const [loadingFirmalar, setLoadingFirmalar] = useState(false);
  const [selectedFirma, setSelectedFirma] = useState(null);
  const [firmaTypeId, setFirmaTypeId] = useState(1);
  const [eslesmeSeviyesi, setEslesmeSeviyesi] = useState(1);
  const [firmaTypes, setFirmaTypes] = useState([]);

  // Backlog #21 — load the real firma_type list once when the modal opens, instead
  // of a hardcoded (and wrong) local map.
  useEffect(() => {
    if (!isOpen) return;
    let ignore = false;
    getFirmaTypes({ forceLive: true })
      .then((types) => { if (!ignore) setFirmaTypes(types); })
      .catch((err) => console.error('Firma turleri yuklenemedi:', err));
    return () => { ignore = true; };
  }, [isOpen]);

  const firmaTypeLabel = useCallback((id) => {
    const found = firmaTypes.find((t) => t.firmaTypeId === id);
    return found ? found.label : `Tip ${id}`;
  }, [firmaTypes]);

  const loadFirmalar = useCallback(async (searchText) => {
    setLoadingFirmalar(true);
    try {
      const data = await standService.getFirmaListForMapping(fuarId, searchText);
      setFirmaList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Firma listesi yuklenemedi:', err);
      setFirmaList([]);
    } finally {
      setLoadingFirmalar(false);
    }
  }, [fuarId]);

  // Modal acildiginda tum firmalari yukle
  useEffect(() => {
    async function run() {
      if (isOpen) {
        await loadFirmalar('');
      }
    }
    run();
  }, [isOpen, loadFirmalar]);

  // Arama degisince debounce ile ara
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      loadFirmalar(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, isOpen, loadFirmalar]);

  // Modal kapaninca sifirla
  useEffect(() => {
    async function run() {
      if (!isOpen) {
        setSearch('');
        setSelectedFirma(null);
        setFirmaTypeId(1);
        setEslesmeSeviyesi(1);
        dispatch(clearEsleState());
      }
    }
    run();
  }, [isOpen, dispatch]);

  // Basarili esleme sonrasi kapat
  useEffect(() => {
    if (esle.success) {
      const timer = setTimeout(() => {
        onSuccess && onSuccess();
        toggle();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [esle.success, onSuccess, toggle]);

  const handleEkle = () => {
    if (!selectedFirma) return;
    dispatch(
      esleStandFirma({
        standId,
        firmaId: selectedFirma.firmaId,
        fuarId,
        firmaTypeId,
        salonId,
        eslesmeSeviyesi,
      })
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Firma Esle</ModalHeader>
      <ModalBody>
        {esle.success && (
          <Alert color="success" className="py-2">
            <i className="ri-check-line me-1" />
            Firma basariyla eslendi!
          </Alert>
        )}
        {esle.error && (
          <Alert color="danger" className="py-2">
            {String(esle.error)}
          </Alert>
        )}

        {/* Firma Bilgisi baslik */}
        <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
          <i className="ri-file-copy-line text-muted fs-5" />
          <span className="fw-semibold">Firma Bilgisi</span>
        </div>

        <Row className="g-3 mb-3">
          <Col md={12}>
            <FormGroup className="mb-0">
              <Label className="fw-medium">Firma Adi</Label>
              <Input
                type="text"
                placeholder="Firma adi ile ara..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSelectedFirma(null);
                }}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup className="mb-0">
              <Label className="fw-medium">Firma Turu:</Label>
              <Input
                type="select"
                value={firmaTypeId}
                onChange={(e) => setFirmaTypeId(Number(e.target.value))}
              >
                <option value="">Seciniz</option>
                {firmaTypes.map((t) => (
                  <option key={t.firmaTypeId} value={t.firmaTypeId}>{t.label}</option>
                ))}
              </Input>
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup className="mb-0">
              <Label className="fw-medium">Eslesme Seviyesi:</Label>
              <Input
                type="select"
                value={eslesmeSeviyesi}
                onChange={(e) => setEslesmeSeviyesi(Number(e.target.value))}
              >
                <option value="">Seciniz</option>
                {ESLESME_SEVIYELERI.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        {/* Firma listesi */}
        {loadingFirmalar ? (
          <div className="text-center py-3">
            <Spinner size="sm" className="me-2" />
            Firmalar yukleniyor...
          </div>
        ) : (
          <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
            <Table size="sm" hover className="mb-0 align-middle">
              <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Firma Adi</th>
                  <th>Turu</th>
                </tr>
              </thead>
              <tbody>
                {firmaList.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-3">
                      {search ? 'Sonuc bulunamadi' : 'Kayit bulunamadi'}
                    </td>
                  </tr>
                ) : (
                  firmaList.map((firma) => (
                    <tr
                      key={firma.firmaId}
                      onClick={() => setSelectedFirma(firma)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor:
                          selectedFirma?.firmaId === firma.firmaId ? '#e8f4fd' : '',
                      }}
                    >
                      <td>
                        <Input
                          type="radio"
                          checked={selectedFirma?.firmaId === firma.firmaId}
                          onChange={() => setSelectedFirma(firma)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="fw-medium">{firma.firmaName}</td>
                      <td>
                        <span className="text-muted small">
                          {firmaTypeLabel(firma.firmaTypeId)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        {selectedFirma && (
          <div className="mt-2 p-2 bg-light rounded small border">
            <i className="ri-check-double-line text-success me-1" />
            <strong>Secilen:</strong> {selectedFirma.firmaName}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          color="primary"
          style={{ backgroundColor: '#3d5a80', borderColor: '#3d5a80' }}
          onClick={handleEkle}
          disabled={!selectedFirma || esle.loading || esle.success}
        >
          {esle.loading ? (
            <><Spinner size="sm" className="me-1" /> Ekleniyor...</>
          ) : (
            <><i className="ri-check-line me-1" /> Ekle</>
          )}
        </Button>
        <Button color="secondary" outline onClick={toggle}>
          Iptal
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FirmaEsleModal;

